import { notification, Row, Spin, Typography } from 'antd';
import { Loader } from 'components/common/Loader';
import { useField, useFormikContext } from 'formik';
import { InvOnHandPayload, InvOnHandResp } from 'models/DeploymentRequestConfig';
import { ProductPayload } from 'models/Product';
import { SparingRequestPayload, SublinePayload } from 'models/SparingRequests';
import { FC } from 'react';
import { useGetSparingRequestConfigurationTypesQuery } from 'redux/services/calvin/sparingRequestConfigTypes';
import { useGetWarehouseAddressesQuery } from 'redux/services/chuckieSue/addressesApi';
import { LocationAddressTypes } from 'redux/services/chuckieSue/models/addresses';
import { useGetInventoryQuery } from 'redux/services/mrJohnson/mrJohnsonApi';
import { useAppSelector } from 'redux/store';
import { DeliveryOptions } from './DeliveryOptions';

export type Props = {
  record: ProductPayload;
  idx: number;
};

export const LocalDeliveryRow: FC<Props> = ({ record, idx }) => {
  const { values } = useFormikContext<SparingRequestPayload>();
  const { fullShippingAddress } = useAppSelector((state) => state.form);
  const [{ value }, , { setValue }] = useField<SublinePayload[]>(`lineItems[${idx}].subLines`);

  // Get sparing request type configuration to check delivery options
  const { data: typesData } = useGetSparingRequestConfigurationTypesQuery();
  const currentType = typesData?.data.find((type) => type.id === values.sparingRequestTypeId);

  const found = value?.find(
    (item) =>
      item.serviceLevelAgreement.toLowerCase() === 'Rush2Hour'.toLowerCase() ||
      item.serviceLevelAgreement.toLowerCase() === 'Rush3Hour'.toLowerCase() ||
      item.serviceLevelAgreement.toLowerCase() === 'Rush4Hour'.toLowerCase() ||
      item.serviceLevelAgreement.toLowerCase() === 'WillCall'.toLowerCase()
  );

  const handleGetTotalQuantity = (allInventoryTotals: SublinePayload[]): number => {
    if (allInventoryTotals.length === 0) return 0;
    else {
      const sumTotal = (arr: SublinePayload[]): number => arr.reduce((sum, { quantity }) => sum + (quantity ?? 0), 0);
      const totalInventory = sumTotal(allInventoryTotals);

      return totalInventory;
    }
  };

  const handleGetTotalQuantityAvailable = (allInventoryTotals: InvOnHandResp[]): number => {
    if (allInventoryTotals.length === 0) return 0;
    else {
      // Simple sum of all warehouse quantities
      const sumTotal = (arr: InvOnHandResp[]): number => {
        return arr.reduce((sum, { quantityAvailable }) => {
          const numValue = Number(quantityAvailable);

          return sum + (isNaN(numValue) ? 0 : numValue);
        }, 0);
      };

      const totalInventory = sumTotal(allInventoryTotals);

      return totalInventory;
    }
  };

  // Base payload for all inventory queries
  const basePayload: InvOnHandPayload = {
    shippingAddress: {
      city: fullShippingAddress?.city || '',
      code: fullShippingAddress?.code,
      country: fullShippingAddress?.country,
      countryAlpha2Code: fullShippingAddress?.countryAlpha2Code || '',
      id: fullShippingAddress?.id,
      latitude: fullShippingAddress?.latitude?.toString(),
      longitude: fullShippingAddress?.longitude?.toString(),
      name: fullShippingAddress?.name,
      postalCode: fullShippingAddress?.postalCode || '',
      street1: fullShippingAddress?.street1 || '',
      street2: fullShippingAddress?.street2 || undefined,
      subdivision: fullShippingAddress?.subdivision,
      subdivisionLocalCode: fullShippingAddress?.subdivisionLocalCode
    },
    sparingDeliveryOption: ''
  };

  // Multiple API calls for different delivery options
  const {
    data: localPickupData,
    isLoading: localPickupLoading,
    isError: localPickupError
  } = useGetInventoryQuery(
    {
      product: record.productNumber.toUpperCase(),
      typeId: values.sparingRequestTypeId as string,
      payload: {
        ...basePayload,
        sparingDeliveryOption: 'LocalPickup'
      } as InvOnHandPayload
    },
    { skip: !values.sparingRequestTypeId || !fullShippingAddress }
  );

  const {
    data: twoHourData,
    isLoading: twoHourLoading,
    isError: twoHourError
  } = useGetInventoryQuery(
    {
      product: record.productNumber.toUpperCase(),
      typeId: values.sparingRequestTypeId as string,
      payload: {
        ...basePayload,
        sparingDeliveryOption: 'Rush2Hour'
      } as InvOnHandPayload
    },
    { skip: !values.sparingRequestTypeId || !fullShippingAddress }
  );

  const {
    data: threeHourData,
    isLoading: threeHourLoading,
    isError: threeHourError
  } = useGetInventoryQuery(
    {
      product: record.productNumber.toUpperCase(),
      typeId: values.sparingRequestTypeId as string,
      payload: {
        ...basePayload,
        sparingDeliveryOption: 'Rush3Hour'
      } as InvOnHandPayload
    },
    { skip: !values.sparingRequestTypeId || !fullShippingAddress }
  );
  const { data: addressesData } = useGetWarehouseAddressesQuery(
    {
      addressCodeContains: localPickupData?.[0]?.warehouseId,
      addressTypesInclude: [LocationAddressTypes.Warehouse],
      offset: 0
    },
    { skip: !localPickupData?.[0]?.warehouseId }
  );

  const shouldExcludePremiumOptions = (): boolean => {
    return addressesData?.data[0]?.excludeFromPremiumLocalDelivery ?? false;
  };

  // SMART ALLOCATION (display-only) and VALIDATION (business rules)
  const calculateRemaining = ({
    rawTwoHour,
    rawThreeHour,
    rawFourHour,
    twoHourQuantity,
    threeHourQuantity,
    fourHourQuantity
  }: {
    rawTwoHour: number;
    rawThreeHour: number;
    rawFourHour: number;
    twoHourQuantity: number;
    threeHourQuantity: number;
    fourHourQuantity: number;
  }): { twoHourRemaining: number; threeHourRemaining: number; fourHourRemaining: number } => {
    // Independent stock
    // Two-hour independent stock equals rawTwoHour (not used directly beyond documentation)
    const threeHourIndependent = Math.max(0, rawThreeHour - rawTwoHour);
    const fourHourIndependent = Math.max(0, rawFourHour - rawThreeHour);

    let remaining2hr = rawTwoHour;
    let remaining3hr = rawThreeHour;
    let remaining4hr = rawFourHour;

    // Step 1: Apply 2hr usage (overlaps with 3hr and 4hr)
    remaining2hr -= twoHourQuantity;
    remaining3hr -= twoHourQuantity;
    remaining4hr -= twoHourQuantity;

    // Step 2: Apply 3hr usage
    const threeExcess = Math.max(0, threeHourQuantity - threeHourIndependent);

    remaining3hr -= threeHourQuantity;
    remaining4hr -= threeHourQuantity;
    remaining2hr -= threeExcess;

    // Step 3: Apply 4hr usage
    const fourExcess = Math.max(0, fourHourQuantity - fourHourIndependent);

    remaining4hr -= fourHourQuantity;
    remaining3hr -= fourExcess;
    remaining2hr -= fourExcess;

    return {
      twoHourRemaining: Math.max(0, remaining2hr),
      threeHourRemaining: Math.max(0, remaining3hr),
      fourHourRemaining: Math.max(0, remaining4hr)
    };
  };

  const validateSelection = (
    newQty: number,
    deliveryType: 'rush2hour' | 'rush3hour' | 'rush4hour',
    rawValues: { twoHour: number; threeHour: number; fourHour: number },
    orderQty: number
  ): string | null => {
    const rawLimit = deliveryType === 'rush2hour' ? rawValues.twoHour : deliveryType === 'rush3hour' ? rawValues.threeHour : rawValues.fourHour;

    if (newQty > rawLimit) {
      return `Only ${rawLimit} available for ${deliveryType}`;
    }

    if (newQty > orderQty) {
      return 'Cannot exceed ordered quantity';
    }

    return null;
  };

  const handleQuantityChange = (index: number, newQuantity: number): void => {
    // Build the same options array that matches filteredOptions structure
    const allPossibleOptions = [...(currentType?.is2HourDeliveryEnabled ? ['Rush2Hour'] : []), ...(currentType?.is3HourDeliveryEnabled ? ['Rush3Hour'] : []), 'Rush4Hour'];

    // Apply the same filtering logic as filteredOptions
    const options = shouldExcludePremiumOptions() ? ['Rush4Hour'] : allPossibleOptions;

    const selectedOption = options[index];

    if (!selectedOption) return;

    // Calculate total quantity if this change is applied
    const currentTotal = handleGetTotalQuantity(value ?? []);
    const currentOptionQuantity = value?.find((item) => item.serviceLevelAgreement.toLowerCase() === selectedOption.toLowerCase())?.quantity || 0;
    const newTotal = currentTotal - currentOptionQuantity + newQuantity;

    // Prevent exceeding ordered quantity across all local delivery options
    if (record.quantity && newTotal > record.quantity) {
      notification.error({
        message: 'Error',
        description: `Cannot request more than ordered quantity (${record.quantity}). Current total: ${currentTotal}, trying to set ${selectedOption.replace('Rush', '')} to ${newQuantity}`,
        className: 'custom-class',
        style: { width: 600 },
        duration: 10
      });

      return;
    }

    const selectedKey = selectedOption.toLowerCase() as 'rush2hour' | 'rush3hour' | 'rush4hour';

    // Raw values for validation
    const rawValues = {
      twoHour: handleGetTotalQuantityAvailable(twoHourData || []),
      threeHour: handleGetTotalQuantityAvailable(threeHourData || []),
      fourHour: handleGetTotalQuantityAvailable(localPickupData || [])
    };

    // Validation layer (business rules only)
    const validationError = validateSelection(newQuantity, selectedKey, rawValues, record.quantity || 0);

    if (validationError) {
      notification.error({ message: 'Error', description: validationError, className: 'custom-class', style: { width: 600 }, duration: 10 });

      return;
    }

    // Update the quantity for the selected option
    const nextValue: SublinePayload[] = value ? [...value] : [];
    const existingIdx = nextValue.findIndex((i) => i.serviceLevelAgreement.toLowerCase() === selectedKey);

    if (newQuantity === 0) {
      // Remove entry if quantity is 0
      if (existingIdx >= 0) {
        nextValue.splice(existingIdx, 1);
      }
    } else if (existingIdx >= 0) {
      nextValue[existingIdx] = { ...nextValue[existingIdx], quantity: newQuantity };
    } else {
      nextValue.push({ serviceLevelAgreement: selectedOption, quantity: newQuantity });
    }

    setValue(nextValue);
  };

  // Combine loading states
  const isLoading = localPickupLoading || twoHourLoading || threeHourLoading;

  // Check if any local delivery is available (check raw API data, not remaining)
  // "Not available" should only show when there's no inventory at all, not when all is selected
  const rawFourHour = handleGetTotalQuantityAvailable(localPickupData || []);
  const rawThreeHour = handleGetTotalQuantityAvailable(threeHourData || []);
  const rawTwoHour = handleGetTotalQuantityAvailable(twoHourData || []);
  const hasLocalDeliveryAvailable = Math.max(rawFourHour, rawThreeHour, rawTwoHour) > 0;

  // Get raw API amounts for INPUT LIMITS (max you can type)
  const getRawAvailableCount = (deliveryType: '2hr' | '3hr' | '4hr'): number => {
    switch (deliveryType) {
      case '2hr':
        return rawTwoHour; // Raw API amount for input limit

      case '3hr':
        return rawThreeHour; // Raw API amount for input limit

      case '4hr':
        return rawFourHour; // Raw API amount for input limit

      default:
        return 0;
    }
  };

  // Get smart allocation remaining for DISPLAY (what shows in parentheses)
  const getDisplayRemaining = (deliveryType: '2hr' | '3hr' | '4hr'): number => {
    const twoHourQuantity = value?.find((item) => item.serviceLevelAgreement.toLowerCase() === 'rush2hour')?.quantity || 0;
    const threeHourQuantity = value?.find((item) => item.serviceLevelAgreement.toLowerCase() === 'rush3hour')?.quantity || 0;
    const fourHourQuantity = value?.find((item) => item.serviceLevelAgreement.toLowerCase() === 'rush4hour')?.quantity || 0;

    const { twoHourRemaining, threeHourRemaining, fourHourRemaining } = calculateRemaining({
      rawTwoHour,
      rawThreeHour,
      rawFourHour,
      twoHourQuantity,
      threeHourQuantity,
      fourHourQuantity
    });

    switch (deliveryType) {
      case '2hr':
        return twoHourRemaining;

      case '3hr':
        return threeHourRemaining;

      case '4hr':
        return fourHourRemaining;

      default:
        return 0;
    }
  };

  const allOptions = [
    ...(currentType?.is2HourDeliveryEnabled
      ? [
          {
            label: '2hr',
            availableCount: getRawAvailableCount('2hr'),
            displayRemaining: getDisplayRemaining('2hr'),
            quantity: value?.find((i) => i.serviceLevelAgreement.toLowerCase() === 'rush2hour')?.quantity || 0,
            disabled: false
          }
        ]
      : []),
    ...(currentType?.is3HourDeliveryEnabled
      ? [
          {
            label: '3hr',
            availableCount: getRawAvailableCount('3hr'),
            displayRemaining: getDisplayRemaining('3hr'),
            quantity: value?.find((i) => i.serviceLevelAgreement.toLowerCase() === 'rush3hour')?.quantity || 0,
            disabled: false
          }
        ]
      : []),
    {
      label: '4hr',
      availableCount: getRawAvailableCount('4hr'),
      displayRemaining: getDisplayRemaining('4hr'),
      quantity: value?.find((i) => i.serviceLevelAgreement.toLowerCase() === 'rush4hour')?.quantity || 0,
      disabled: false
    }
  ];

  const filteredOptions = shouldExcludePremiumOptions() ? allOptions.filter((option) => option.label === '4hr') : allOptions;

  if (handleGetTotalQuantity(value ?? []) === record.quantity && !found)
    return (
      <Row style={{ height: 75, width: '100%' }} align={'middle'} justify={'center'}>
        <Typography.Text>Max quantity reached</Typography.Text>
      </Row>
    );

  // Show shorter message when no local delivery is available
  if (!isLoading && !hasLocalDeliveryAvailable) {
    return (
      <Row style={{ height: 75, width: '100%' }} align={'middle'} justify={'center'}>
        <Typography.Text>Not available</Typography.Text>
      </Row>
    );
  }

  return (
    <Spin spinning={isLoading} indicator={<Loader small />} style={{ width: '100%' }}>
      <div className="mr-4" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
        <DeliveryOptions options={filteredOptions} onQuantityChange={handleQuantityChange} />
      </div>
    </Spin>
  );
};
